import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import BBCodeParser from '../helpers/BBCodeRenderer';
import LoadingInline from './loading-inline.js';
import BggGameModal from './bgg-game-modal.js';
import { useGeeklist } from '../custom-hooks';
import { 
    COLUMN_NAMES, 
    FILTER_TYPES, 
    STATUS, 
    MAX_BULK_PRICE_LOOKUP,
    PRICE_TYPES 
} from '../constants';
import { AiOutlineInfoCircle, AiOutlineCheckSquare, AiOutlineUndo, AiOutlineDelete, AiOutlineExpand, AiOutlineCompress, AiOutlineControl, AiOutlineEye, AiOutlineThunderbolt, AiOutlineCopy } from "react-icons/ai";
import { LuFilter, LuFilterX } from "react-icons/lu";
import * as renderers from './cell-renderers/index.js';
import * as comparators from './comparators/index.js';
import SlidePanel from './slide-panel.js';
import { useContext } from 'react';
import { GlobalStateContext } from '../GlobalState';
import { toast } from 'react-toastify';

/**
 * BggList Component
 * 
 * Display component for rendering a BGG geeklist with filtering and sorting capabilities.
 * All business logic is handled by the useGeeklist custom hook.
 */
const BggList = () => {
    const [searchParams] = useSearchParams();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalGameData, setModalGameData] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [isGeeklistBodyExpanded, setIsGeeklistBodyExpanded] = useState(false);
    const [zipCode, setZipCode] = useState('');

    const dropdownRef = useRef(null);
    const gridRef = useRef();

    // Get all state and operations from the custom hook
    const {
        geeklistMeta,
        geeklistItems,
        geeklists,
        isLoading,
        progressValues,
        rowCount,
        showSelectedOnly,
        hasSelectedRows,
        showAdvanced,
        itemSearchTerm,
        hiddenRows,
        controlState,
        isGridExpanded,
        usernameValue,
        setShowAdvanced,
        setItemSearchTerm,
        setControlState,
        setIsGridExpanded,
        setUsernameValue,
        setHiddenRows,
        setListId,
        fetchWishlistData,
        clearWishlist,
        updateDistances,
        updateDealScore,
        updateBulkDealScores,
        isExternalFilterPresent,
        doesExternalFilterPass,
        handleSoldFilterClick,
        updateRowCount,
        clearAllSelections,
        clearAllFilters,
        toggleSelectedFilter,
        selectAll,
        filterToSeller,
        updateUrlWithSelectedItems,
    } = useGeeklist(gridRef);

    // Get GlobalState context for row count
    const { setRowCount: setGlobalRowCount } = useContext(GlobalStateContext);
    
    // Update GlobalState row count when local row count changes
    useEffect(() => {
        setGlobalRowCount(rowCount);
    }, [rowCount, setGlobalRowCount]);

    // Handle click outside dropdown to close it
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Event handlers
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setShowDropdown(true);
    };

    const handleSearchKeyDown = (event) => {
        if (event.key === 'Enter') {
            const value = event.target.value.trim();
            if (/^\d+$/.test(value)) {
                setListId(value);
                const newParams = new URLSearchParams(searchParams);
                newParams.set('geeklistId', value);
                window.history.replaceState(null, '', window.location.pathname + '?' + newParams.toString());
            }
            setShowDropdown(false);
        }
    };

    const handleGeeklistSelect = (geeklist) => {
        setListId(geeklist.id);
        setSearchTerm(geeklist.title);
        setShowDropdown(false);
        const newParams = new URLSearchParams(searchParams);
        newParams.set('geeklistId', geeklist.id);
        window.history.replaceState(null, '', window.location.pathname + '?' + newParams.toString());
    };

    const handleUsernameValue = (event) => setUsernameValue(event.target.value);

    // Copy filtered data to clipboard
    const handleCopyFilteredData = () => {
        if (!gridRef.current || !gridRef.current.api) return;
        
        const filteredItems = [];
        gridRef.current.api.forEachNodeAfterFilter(node => {
            filteredItems.push(node.data);
        });
        
        // Create CSV format
        const headers = ['Game Name', 'Price', 'Type', 'Seller', 'Post Date', 'Deal Score'];
        const csvRows = [headers.join(',')];
        
        filteredItems.forEach(item => {
            const row = [
                `"${item.item?.name?.replace(/"/g, '""') || ''}"`,
                `$${parseFloat(item.price).toFixed(2)}` || '',
                item.priceType || '',
                item.username || '',
                item.postdate || '',
                item.dealScore.score === '' ? 
                    '' : 
                    `$${parseFloat(item.dealScore.averagePrice).toFixed(2)} (${parseFloat(item.dealScore.score).toFixed(0)}%)` 
            ];
            csvRows.push(row.join(','));
        });
        
        const csvContent = csvRows.join('\n');
        
        // Copy to clipboard
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(csvContent).then(() => {
                alert(`Copied ${filteredItems.length} filtered items to clipboard (CSV format)`);
            }).catch(err => {
                console.error('Copy failed:', err);
                alert('Failed to copy data');
            });
        }
    };

    const handleCopyFilteredDataBBCode = () => {
        if (!gridRef.current || !gridRef.current.api) return;
        
        const filteredItems = [];
        gridRef.current.api.forEachNodeAfterFilter(node => {
            filteredItems.push(node.data);
        });

        const csvRows = [];

        filteredItems.forEach(item => {
            var priceTypeString = null
            if(![PRICE_TYPES.BUY_IT_NOW, PRICE_TYPES.FIXED_PRICE, PRICE_TYPES.UNSTRUCTURED].includes(item.priceType)){
                priceTypeString = `(${item.priceType})`;
            }
            const row = [
                `[url=${item.link}]${item.item.name}[/url]`,
                `$${parseFloat(item.price).toFixed(2)} ${priceTypeString ? priceTypeString : ''}` || '',
                
            ];
            csvRows.push(row.join('\t'));
        });

        const csvContent = csvRows.join('\n').concat(`\n\n[url=${window.location.href}]See all of them here[/url]`);

        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(csvContent).then(() => {
                toast.success(`Copied ${filteredItems.length} filtered items to clipboard (BBCode format)`);
            }).catch(err => {
                console.error('Copy failed:', err);
                toast.error('Failed to copy data');
            });
        }
    }

    const getFilteredGeeklists = (term) => {
        if (!term.trim()) return geeklists;
        return geeklists.filter(geeklist => 
            geeklist.title.toLowerCase().includes(term.toLowerCase()) || 
            geeklist.id.includes(term)
        );
    };

    const openModal = () => setIsModalOpen(true);

    // Grid ready handler
    const readyGrid = (params) => {
        updateRowCount();
        const filterModel = { sold: { filterType: 'text', type: 'equals', filter: 'false' } };
        
        const seller = searchParams.get('seller');
        if (seller && geeklistItems.length > 0) {
            filterModel.username = { filterType: 'text', type: 'equals', filter: seller };
        }

        const selectedParam = searchParams.get('selected');
        if (selectedParam && geeklistItems.length > 0) {
            const selectedIds = selectedParam.split(',');
            const updatedItems = [...geeklistItems];
            updatedItems.forEach(item => {
                item.selected = selectedIds.includes(item.id) ? STATUS.TRUE : STATUS.FALSE;
            });
            if (selectedIds.length > 0) {
                filterModel.selected = { filterType: 'text', type: 'equals', filter: 'true' };
            }
        }
        
        params.api.setFilterModel(filterModel);
        params.api.onFilterChanged();
        params.api.applyColumnState({
            state: [{ colId: 'editdate', sort: 'desc' }],
            defaultState: { sort: null }
        });
    };

    // Column definitions
    const columnDefs = [
        { headerName: COLUMN_NAMES.SELECT, width: 55, minWidth: 55, field: 'selected', cellRenderer: 'checkboxRenderer', sortable: true, filter: true, pinned: 'left', cellClass: 'select-cell'},
        { headerName: COLUMN_NAMES.NAME, field: 'itemName', valueGetter: (cell) => cell.data.item.name, flex: 2, minWidth: 220, filter: true, cellRenderer: renderers.GameLinkRenderer, cellRendererParams: { openModal, setModalGameData }, width: 300 },
        { headerName: COLUMN_NAMES.PRICE, field: 'price', flex: 1, minWidth: 80, filter: FILTER_TYPES.NUMBER, comparator: comparators.priceComparator, cellRenderer: renderers.PriceRenderer },
        { headerName: COLUMN_NAMES.TYPE, field: 'priceType', flex: 1, minWidth: 80, filter: true },
        // { headerName: COLUMN_NAMES.LISTING_INFO, field: 'body', flex: 1, minWidth: 150, cellRenderer: renderers.ButtonRenderer, cellRendererParams: { openModal, setModalGameData } },
        { headerName: COLUMN_NAMES.AVERAGE_PRICE, field: 'dealScore', flex: 1, minWidth: 120, filter: FILTER_TYPES.NUMBER, cellRenderer: renderers.DealScoreRenderer, cellRendererParams: { updateDealScore }, width: 80, comparator: comparators.dealScoreComparator, hide: !showAdvanced },
        { headerName: COLUMN_NAMES.EXTRAS, field: 'extrasIds', valueGetter: (cell) => cell.data.extrasIds.length, flex: 1, minWidth: 60, filter: FILTER_TYPES.NUMBER, width: 90, hide: !showAdvanced },
        { headerName: COLUMN_NAMES.MATCHES, field: 'matches', flex: 1, minWidth: 60, filter: true, cellRenderer: renderers.OtherListingsCellRenderer, width: 60, comparator: comparators.OtherListingComparator, hide: !showAdvanced },
        { headerName: COLUMN_NAMES.AVG_RATING, field: 'stats.average', flex: 1, minWidth: 60, filter: FILTER_TYPES.NUMBER, cellRenderer: renderers.LeftAlignedCellRenderer, width: 60, hide: !showAdvanced },
        { headerName: COLUMN_NAMES.SELLER, field: 'username', flex: 1, minWidth: 120, filter: true, comparator: comparators.lowercaseComparator, cellRenderer: renderers.SellerCellRenderer, cellRendererParams: { filterToSeller, gridRef } },
        { headerName: COLUMN_NAMES.LIST_DATE, field: 'postdate', flex: 1, minWidth: 120, filter: FILTER_TYPES.DATE, cellRenderer: renderers.DateRenderer, width: 120, hide: !showAdvanced },
        { headerName: COLUMN_NAMES.EDIT_DATE, field: 'editdate', flex: 1, minWidth: 120, filter: FILTER_TYPES.DATE, cellRenderer: renderers.DateRenderer, width: 120, hide: !showAdvanced },
        { headerName: COLUMN_NAMES.END_DATE, field: 'endDate', flex: 1, minWidth: 120, filter: FILTER_TYPES.DATE, comparator: comparators.endDateComparator, cellRenderer: renderers.EndDateRenderer, width: 120, hide: !showAdvanced },
        { headerName: COLUMN_NAMES.SOLD, field: 'sold', flex: 1, minWidth: 120, filter: true, cellRenderer: renderers.LeftAlignedCellRenderer, width: 80 },
        { headerName: COLUMN_NAMES.EXPANSION, field: 'expansion', flex: 1, minWidth: 120, filter: true, cellRenderer: renderers.BooleanCellRenderer, width: 80, hide: !showAdvanced },
        { headerName: COLUMN_NAMES.LOCATION, field: 'location', flex: 1, minWidth: 120, filter: true, comparator: comparators.LocationComparator, cellRenderer: renderers.LocationRenderer, width: 80, hide: !showAdvanced },
    ];

    const filteredGeeklists = getFilteredGeeklists(searchTerm);

    return (
        <div className="app-container" style={isGridExpanded ? { marginBottom: 0 } : undefined}>
            <BggGameModal
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                modalData={modalGameData}
                onFilterToSeller={(seller) => filterToSeller(seller)()}
                updateDealScore={updateDealScore}
            />

            {/* Top search bar */}
            {!isGridExpanded && (
                <div className="controls-top-bar">
                    <div ref={dropdownRef} className="dropdown-container" style={{ position: 'relative' }}>
                        <input 
                            id="geeklistSearchInput" 
                            type="text" 
                            value={searchTerm} 
                            onChange={handleSearchChange}
                            onKeyDown={handleSearchKeyDown}
                            onFocus={() => setShowDropdown(true)}
                            className="control-input"
                            placeholder='Enter geeklist ID or search by title...'
                            style={{ width: '100%' }}
                        />
                        <div className="clear-button" onClick={() => setSearchTerm('')}>
                            <i className="fa-solid fa-xmark"></i>
                        </div>
                        {showDropdown && (
                            <div className="dropdown-list">
                                {filteredGeeklists.length > 0 ? (
                                    filteredGeeklists.map(geeklist => (
                                        <div 
                                            key={geeklist.id} 
                                            className="dropdown-item"
                                            onClick={() => handleGeeklistSelect(geeklist)}
                                            style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                                        >
                                            <div style={{ fontWeight: 'bold' }}>{geeklist.title}</div>
                                            <div style={{ fontSize: '0.8em', color: '#666' }}>
                                                ID: {geeklist.id} | QTY: {geeklist.numItems} | Created by: {geeklist.username}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div style={{ padding: '8px 12px', color: '#666' }}>
                                        {searchTerm.trim() !== '' ? 'No matching recent geeklists found. Hit enter to load by geeklist ID' : 'Start typing to search recent geeklists'}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <button 
                        className="icon-button icon-menu-button info-button" 
                        onClick={() => setIsGeeklistBodyExpanded(!isGeeklistBodyExpanded)}
                        disabled={geeklistItems.length === 0}
                        title={isGeeklistBodyExpanded ? 'Hide Description' : 'Show Description'}
                    >
                        <AiOutlineInfoCircle />
                    </button>
                </div>
            )}

            {/* Controls panel */}
            <SlidePanel isOpen={controlState} onClose={() => setControlState(false)} title="Controls" width="320px">
                <div className="control-group" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                    <input 
                        id="usernameInput" 
                        type="text" 
                        placeholder="BGG Username" 
                        value={usernameValue} 
                        onChange={handleUsernameValue}
                        className="control-input" 
                    />
                    <button onClick={fetchWishlistData} disabled={geeklistItems.length === 0} className="control-button secondary-button" style={{ width: '100%' }}>
                        Filter by Wishlist
                    </button>
                    <button onClick={clearWishlist} disabled={geeklistItems.length === 0} className="control-button secondary-button" style={{ width: '100%' }}>
                        Clear Wishlist
                    </button>
                </div>

                <div className="control-divider" style={{ width: '100%', height: '1px' }}></div>

                <div className="control-group" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                    <button onClick={() => setShowAdvanced(!showAdvanced)} className="secondary-button control-button" disabled={geeklistItems.length === 0} style={{ width: '100%' }}>
                        {showAdvanced ? 'Hide Advanced Data' : 'Show Advanced Data'}
                    </button>
                    <button onClick={handleSoldFilterClick} className="secondary-button control-button" disabled={isLoading || geeklistItems.length === 0} style={{ width: '100%' }}>
                        {gridRef.current && gridRef.current.api?.getFilterModel().sold !== undefined ? 'Show Sold' : 'Hide Sold'}
                    </button>
                    <button 
                            className='secondary-button control-button' 
                            style={{ width: '100%' }}
                            onClick={handleCopyFilteredData} 
                            title='Copy filtered data (CSV)'
                            disabled={isLoading || geeklistItems.length === 0}
                        >
                            <AiOutlineCopy /> Copy Table Data
                        </button>
                        <button 
                            className='secondary-button control-button' 
                            style={{ width: '100%' }}
                            onClick={handleCopyFilteredDataBBCode} 
                            title='Copy filtered data (BBCode)'
                            disabled={isLoading || geeklistItems.length === 0}
                        >
                            <AiOutlineCopy /> Copy Table Data as BBCode Links
                        </button>
                    {showAdvanced && (
                        <>
                            <button onClick={updateBulkDealScores} className="secondary-button control-button" disabled={isLoading || geeklistItems.length === 0 || rowCount > MAX_BULK_PRICE_LOOKUP} style={{ width: '100%' }}>
                                Bulk Lookup Prices
                            </button>
                            <input type="text" value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="Zip code or city, state" className="control-input" />
                            <button onClick={() => updateDistances(zipCode)} className="secondary-button control-button" disabled={isLoading || geeklistItems.length === 0 || zipCode.trim() === ''} style={{ width: '100%' }}>
                                Apply Distance
                            </button>
                        </>
                    )}
                </div>
            </SlidePanel>
            
            {/* Geeklist description panel */}
            {geeklistMeta && geeklistMeta.body && (
                <SlidePanel isOpen={isGeeklistBodyExpanded} onClose={() => setIsGeeklistBodyExpanded(false)} title={geeklistMeta.name || 'Geeklist Description'} width="480px">
                    <div className="geeklist-body" style={{ padding: '0 16px 16px' }}>
                        <BBCodeParser bbcode={geeklistMeta.body} />
                    </div>
                </SlidePanel>
            )}
            
            {isLoading && <LoadingInline message={progressValues.message} progress={progressValues.progress} type={progressValues.type} />}

            {geeklistItems.length > 0 && !isLoading && !isGridExpanded && (
                <div className="search-container">
                    <input 
                        type="text" 
                        value={itemSearchTerm} 
                        onChange={(e) => setItemSearchTerm(e.target.value)} 
                        placeholder="Search by game name, description, or seller."
                    />
                </div>
            )}

            {!isLoading && (
                <div className="table-info">
                    <div className="row-count">
                        <button 
                            onClick={hasSelectedRows ? toggleSelectedFilter : selectAll} 
                            className="icon-button" 
                            disabled={isLoading || geeklistItems.length === 0}
                            title={hasSelectedRows ? (!showSelectedOnly ? 'Show selected only' : 'Show all') : 'Select all'}
                        >
                            {!hasSelectedRows ? <AiOutlineCheckSquare /> : (!showSelectedOnly ? <LuFilter /> : <LuFilterX />)}
                        </button>
                        {hasSelectedRows && (
                            <button onClick={clearAllSelections} className="icon-button" disabled={isLoading || geeklistItems.length === 0}>
                                <AiOutlineDelete />
                            </button>
                        )}
                        <div className="vertical-separator"></div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {!!showAdvanced &&
                            <button className='icon-button' onClick={updateBulkDealScores} title='Bulk Update Deal Scores' disabled={isLoading || geeklistItems.length === 0 || rowCount > MAX_BULK_PRICE_LOOKUP}>
                                <AiOutlineThunderbolt />
                            </button>
                        }
                        <button className='icon-button' onClick={clearAllFilters} title='Clear All Filters'>
                            <AiOutlineUndo />
                        </button>
                        <button className='icon-button' onClick={() => setShowAdvanced(!showAdvanced)} title='Show Advanced Fields'>
                            <AiOutlineEye />
                        </button>
                        <button className='icon-button' onClick={() => setIsGridExpanded(!isGridExpanded)} title={isGridExpanded ? 'Collapse' : 'Expand'}>
                            {isGridExpanded ? <AiOutlineCompress /> : <AiOutlineExpand />}
                        </button>
                        <button className='icon-button' onClick={() => setControlState(!controlState)} title='Controls'>
                            <AiOutlineControl />
                        </button>
                    </div>
                </div>
            )}

            {geeklistItems.length === 0 && (
                <div className="no-geeklist-message">
                    <p>Please use the input above to enter a geeklist id, or select a recent geeklist.</p>
                    <p>Click the about button <AiOutlineInfoCircle style={{fontSize: 'large'}} /> for more information.</p>
                </div>
            )}
            
            {geeklistItems.length > 0 && (
                <div className="table-container">
                    <div className="ag-theme-alpine">
        <AgGridReact
            ref={gridRef}
            columnDefs={columnDefs}
            autoSizeStrategy={{ type: 'fitCellContents', skipHeader: true }}
            rowHeight={52}
            rowData={geeklistItems}
                            isExternalFilterPresent={isExternalFilterPresent}
                            doesExternalFilterPass={doesExternalFilterPass}
                            onGridReady={readyGrid}
                            onModelUpdated={updateRowCount}
                            className="data-grid"
                            context={{ hiddenRows, setHiddenRows, updateUrlWithSelectedItems }}
                            suppressMenuHide={true}
                            components={{ checkboxRenderer: renderers.CheckboxRenderer }}
                            enableMultiSort={true}
                            multiSortKey={'ctrl'}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default BggList;